/**
 * Grant-based authorization for WayAI.
 *
 * Ported from Rekor with adaptations:
 * - workspaces -> hubs
 * - collections -> hub_tags (OR matching)
 * - WayAI-specific Permission type
 */

import type { AuthContext, Grant } from './permissions';
import { USER_PERMISSIONS } from './permissions';

export interface AccessRequest {
  org_id?: string;
  hub?: string;
  hub_tag?: string;
  environment?: 'production' | 'preview';
  permission: string;
}

/**
 * Check if an auth context allows a specific access request.
 * Uses union semantics: returns true if ANY grant matches.
 */
export function hasAccess(auth: AuthContext, req: AccessRequest): boolean {
  for (const grant of auth.grants) {
    if (grantMatches(grant, req)) {
      return true;
    }
  }
  return false;
}

function grantMatches(grant: Grant, req: AccessRequest): boolean {
  // Check org scope — if both request and grant specify an org, they must match
  if (req.org_id && grant.scope.org_id) {
    if (req.org_id !== grant.scope.org_id) {
      return false;
    }
  }

  // Check hub scope
  if (req.hub) {
    if (!matchesScope(grant.scope.hubs, req.hub)) {
      return false;
    }
  }

  // Check hub_tag scope (undefined on grant = all tags allowed)
  if (req.hub_tag && grant.scope.hub_tags) {
    if (!matchesScope(grant.scope.hub_tags, req.hub_tag)) {
      return false;
    }
  }

  // Check environment scope (undefined on grant = all environments allowed)
  if (req.environment && grant.scope.environments) {
    if (!grant.scope.environments.includes(req.environment)) {
      return false;
    }
  }

  // Check permission
  if (!matchesScope(grant.permissions as string[], req.permission)) {
    return false;
  }

  return true;
}

function matchesScope(allowed: string[], value: string): boolean {
  return allowed.includes('*') || allowed.includes(value);
}

/**
 * Hub-coverage context: everything `grantCoversHub` needs to decide whether
 * a grant covers a specific hub. The hub's actual `org_id` is required —
 * relying on caller-supplied org context lets a wildcard grant in one org
 * silently cover hubs in any other org.
 */
export interface HubScopeContext {
  id: string;
  org_id: string;
  tags?: string[];
  environment?: 'production' | 'preview';
}

/**
 * Single source of truth for "does this grant cover this hub?". Open-coding
 * `grant.scope.hubs.includes(...)` elsewhere risks pairing either branch
 * with no `org_id` check, which lets one org's grant cover any hub in any
 * org.
 *
 * Scope rule (uniform): every grant MUST declare a `scope.org_id` that
 * matches the hub's org. Wildcards without org context would match any
 * hub in any org; specific hub ids without org context let an org-A
 * admin mint a token for a known hub UUID in org B (cross-org bypass).
 *
 * Returns true iff the grant's `scope.org_id` matches the hub's org, the
 * grant covers the hub by id (or wildcard), AND the grant's tag/environment
 * filters all match the hub.
 */
export function grantCoversHub(grant: Grant, hub: HubScopeContext): boolean {
  if (grant.scope.org_id !== hub.org_id) return false;
  if (!grant.scope.hubs.includes('*') && !grant.scope.hubs.includes(hub.id)) {
    return false;
  }

  // Hub-tag filter: grant restricts to certain tags → hub must have at least one.
  if (grant.scope.hub_tags && grant.scope.hub_tags.length > 0) {
    const tags = hub.tags ?? [];
    if (tags.length === 0) return false;
    const overlap = grant.scope.hub_tags.some(t => t === '*' || tags.includes(t));
    if (!overlap) return false;
  }

  // Environment filter: grant restricts to certain envs → hub must match.
  if (hub.environment && grant.scope.environments && !grant.scope.environments.includes(hub.environment)) {
    return false;
  }

  return true;
}

/** The permissions either of which makes a caller an editor of an org's contact book (#4931 owner decision 1). */
export const CONTACT_BOOK_EDITOR_PERMISSIONS = ['org:admin', 'contacts:write'] as const;

/**
 * Whether `auth` may read and write `orgId`'s whole contact book (#4931 owner decision 1): one
 * of `CONTACT_BOOK_EDITOR_PERMISSIONS` over every hub of the org (`hub: '*'`), every hub tag
 * (`hub_tag: '*'`) and both environments. The book holds every hub's contacts, so a grant that
 * stops at some hubs, some tags or one environment edits none of it; a hub admin reads only what
 * the hub sees, through `loadHub`.
 */
export function isContactBookEditor(auth: AuthContext, orgId: string): boolean {
  return CONTACT_BOOK_EDITOR_PERMISSIONS.some((permission) =>
    (['production', 'preview'] as const).every((environment) =>
      hasAccess(auth, { org_id: orgId, hub: '*', hub_tag: '*', environment, permission })));
}

/**
 * Check if child grants are a subset of parent grants.
 * Used to enforce privilege ceiling on token creation.
 * Every access the child grants must also be allowed by the parent.
 */
export function isSubsetOf(parentAuth: AuthContext, childGrants: Grant[]): boolean {
  for (const child of childGrants) {
    // Without scope.org_id, hasAccess can't enforce the org boundary —
    // any minted token would cover hubs cross-org.
    if (!child.scope.org_id) {
      return false;
    }

    const parentCoversOrg = parentAuth.grants.some(
      g => !g.scope.org_id || g.scope.org_id === child.scope.org_id,
    );
    if (!parentCoversOrg) {
      return false;
    }

    // Every hub in child must be covered by parent
    if (!child.scope.hubs.includes('*')) {
      for (const hub of child.scope.hubs) {
        for (const perm of child.permissions) {
          if (!hasAccess(parentAuth, { org_id: child.scope.org_id, hub, permission: perm })) {
            return false;
          }
        }
      }
    } else {
      // Child wants wildcard hubs — parent must also have wildcard hub access for each permission
      for (const perm of child.permissions) {
        if (!hasAccess(parentAuth, { org_id: child.scope.org_id, permission: perm })) {
          return false;
        }
        const parentHasWildcard = parentAuth.grants.some(
          g =>
            g.scope.hubs.includes('*') &&
            (!g.scope.org_id || g.scope.org_id === child.scope.org_id) &&
            matchesScope(g.permissions as string[], perm),
        );
        if (!parentHasWildcard) {
          return false;
        }
      }
    }

    // Check hub_tag scope escalation
    if (!child.scope.hub_tags || child.scope.hub_tags.includes('*')) {
      // Wildcard-including list is equivalent to undefined: matchesScope(['*'], tag)
      // always returns true at runtime, so both forms grant every tag.
      // Empty hub list is wildcard scope (mirrors the environment block below):
      // collapse to ['*'] so a hub-less child still requires parent all-tag coverage.
      const hubs =
        child.scope.hubs.includes('*') || child.scope.hubs.length === 0
          ? ['*']
          : child.scope.hubs;
      for (const hub of hubs) {
        for (const perm of child.permissions) {
          const parentCoversAllTags = parentAuth.grants.some(
            g =>
              (!g.scope.org_id || g.scope.org_id === child.scope.org_id) &&
              matchesScope(g.scope.hubs, hub) &&
              !g.scope.hub_tags &&
              matchesScope(g.permissions as string[], perm),
          );
          if (!parentCoversAllTags) {
            return false;
          }
        }
      }
    } else {
      const hubs =
        child.scope.hubs.includes('*') || child.scope.hubs.length === 0
          ? ['*']
          : child.scope.hubs;
      for (const tag of child.scope.hub_tags) {
        for (const perm of child.permissions) {
          for (const hub of hubs) {
            // org_id is required: grantMatches only enforces the org boundary
            // when the request carries it, so a different-org parent grant
            // covering the same hub/tag/perm would otherwise satisfy this.
            if (
              !hasAccess(parentAuth, {
                org_id: child.scope.org_id,
                hub: hub === '*' ? undefined : hub,
                hub_tag: tag,
                permission: perm,
              })
            ) {
              return false;
            }
          }
        }
      }
    }

    // Check environment scope escalation
    if (!child.scope.environments) {
      // Empty hub list is wildcard scope: requires the parent to hold an
      // all-hub, all-env grant, not a vacuous pass.
      const hubs =
        child.scope.hubs.includes('*') || child.scope.hubs.length === 0
          ? ['*']
          : child.scope.hubs;
      for (const hub of hubs) {
        const parentCoversAllEnvs = parentAuth.grants.some(
          g =>
            (!g.scope.org_id || g.scope.org_id === child.scope.org_id) &&
            !g.scope.environments &&
            matchesScope(g.scope.hubs, hub) &&
            child.permissions.every(p => matchesScope(g.permissions as string[], p)),
        );
        if (!parentCoversAllEnvs) {
          return false;
        }
      }
    }
  }

  return true;
}

/**
 * Narrow a scoped token's grants to what `ceiling` (its owner's current grants) still allows.
 * `authMiddleware` applies it on every request a scoped API token makes, so a grant its owner
 * has lost stops working at once, and works again if the owner regains it.
 *
 * Each result is one token grant intersected with one ceiling grant of the same org, so
 * `hasAccess`, `grantCoversHub` and `requireOrgAccess` pass on the result only where they pass
 * on both inputs. Hubs, tags, environments and permissions each narrow to their overlap; a
 * `'*'` entry, or an absent tag or environment filter, takes the other side. A pair is dropped
 * when either grant has no `org_id` (`hasAccess` would read it as covering every org), or when
 * any axis has no overlap. A token grant always names at least one hub (`createTokenBody`).
 *
 * A token grant inside an org-wide ceiling grant with no filters (an org member's) comes back
 * unchanged.
 */
export function intersectGrants(grants: Grant[], ceiling: Grant[]): Grant[] {
  const result: Grant[] = [];
  for (const grant of grants) {
    for (const bound of ceiling) {
      const narrowed = intersectGrant(grant, bound);
      if (narrowed) result.push(narrowed);
    }
  }
  return result;
}

function intersectGrant(grant: Grant, bound: Grant): Grant | null {
  const orgId = grant.scope.org_id;
  if (!orgId || bound.scope.org_id !== orgId) return null;

  const hubs = intersectScope(grant.scope.hubs, bound.scope.hubs);
  const permissions = intersectScope(grant.permissions, bound.permissions);
  const hubTags = intersectFilter(grant.scope.hub_tags, bound.scope.hub_tags);
  const environments = intersectFilter(grant.scope.environments, bound.scope.environments);
  if (hubs.length === 0 || permissions.length === 0 || hubTags === null || environments === null) {
    return null;
  }

  return {
    scope: {
      org_id: orgId,
      hubs,
      ...(hubTags ? { hub_tags: hubTags } : {}),
      ...(environments ? { environments } : {}),
    },
    permissions,
  };
}

/** The overlap of two `matchesScope` lists: a side holding `'*'` takes the other side. */
function intersectScope<T extends string>(a: T[], b: T[]): T[] {
  if (a.includes('*' as T)) return [...b];
  return a.filter((v) => matchesScope(b, v));
}

/**
 * The overlap of two optional filters, where absent means unrestricted. `null` when both
 * restrict and share nothing: an empty tag list would read as unrestricted to
 * `grantCoversHub`, so it is never returned.
 */
function intersectFilter<T extends string>(a: T[] | undefined, b: T[] | undefined): T[] | undefined | null {
  if (!a) return b ? [...b] : undefined;
  if (!b) return [...a];
  const both = intersectScope(a, b);
  return both.length > 0 ? both : null;
}

/**
 * Expand a "Full access" wildcard permission (`'*'`) on a child grant to the
 * concrete set of user-assignable permissions the parent actually holds for
 * that exact scope (org + hubs + tags + envs).
 *
 * A minted token must NEVER carry the literal `'*'` superuser permission: no
 * human session holds it (`USER_PERMISSIONS` excludes `'*'` and
 * `'platform:admin'`), and storing it would grant the token MORE than its
 * creator has. So "Full access" means "everything I can do here", resolved
 * per-scope from the parent's own grants — which is also why a Full-access
 * request from a platform admin (whose only universal grant is
 * `['platform:admin']`) still resolves to a normal user permission set.
 *
 * Returns the grant unchanged when it carries no wildcard. Reuses `isSubsetOf`
 * per candidate permission so scope semantics (hub/tag/env coverage) stay
 * identical to the ceiling check. An empty result means the caller holds
 * nothing for that scope — the caller MUST reject rather than mint a
 * permission-less token (`isSubsetOf` vacuously passes an empty-permission
 * grant, so the emptiness check can't be delegated to it).
 */
export function expandWildcardPermissions(parentAuth: AuthContext, child: Grant): Grant {
  if (!child.permissions.includes('*')) return child;
  const effective = USER_PERMISSIONS.filter(perm =>
    isSubsetOf(parentAuth, [{ ...child, permissions: [perm] }]),
  );
  return { ...child, permissions: effective };
}
